import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { SavedNote } from "../shared/Icons";
import { RichEditor } from "../wiki/RichEditor";

// Fontes de estoque (self-service): o cliente escolhe a fonte no portal, cola a URL e
// vê o tutorial em "Encontre o link". Só nome + tutorial: a chave o back gera do nome e
// o parser é detectado automaticamente no sync. system_settings["estoque_fontes"].

interface Fonte { key?: string; label: string; tutorial_html: string }

const inp: React.CSSProperties = { padding: "7px 10px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", marginBottom: 4, display: "block" };
const ghost: React.CSSProperties = { padding: "6px 12px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" };
const red: React.CSSProperties = { padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };

export function EstoqueFontesTab() {
  const qc = useQueryClient();
  const { data: saved = [] } = useQuery<Fonte[]>({ queryKey: ["estoque-fontes"], queryFn: () => api.get("/estoque-fontes") });
  const [fontes, setFontes] = useState<Fonte[]>([]);
  const [sel, setSel] = useState<number | null>(null);  // índice: fonte nova ainda não tem chave
  const [error, setError] = useState("");
  useEffect(() => { setFontes(saved); }, [saved]);
  const save = useMutation({
    mutationFn: () => api.put("/estoque-fontes", fontes),
    onSuccess: () => { setError(""); qc.invalidateQueries({ queryKey: ["estoque-fontes"] }); },
    onError: (e: any) => setError(e?.payload?.detail ?? "Erro ao salvar"),
  });
  const dirty = JSON.stringify(fontes) !== JSON.stringify(saved);
  const cur = sel !== null ? fontes[sel] : undefined;
  const upd = (patch: Partial<Fonte>) => setFontes((p) => p.map((f, i) => (i === sel ? { ...f, ...patch } : f)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 1100 }}>
      <div style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>
        Fontes que o cliente escolhe no portal ao conectar o estoque. O formato é detectado sozinho; o tutorial aparece em "Encontre o link".
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {fontes.map((f, i) => (
            <button key={f.key ?? `new-${i}`} onClick={() => setSel(i)}
              style={{ textAlign: "left", padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13, background: sel === i ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px solid ${sel === i ? "var(--brand)" : "var(--line)"}`, color: "var(--ink)" }}>
              {f.label || <span style={{ color: "var(--ink-muted)" }}>sem nome</span>}
            </button>
          ))}
          <button onClick={() => { setFontes((p) => [...p, { label: "", tutorial_html: "" }]); setSel(fontes.length); }} style={ghost}>+ Fonte</button>
        </div>
        {cur ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
            <div><label style={lbl}>Nome (o cliente vê)</label>
              <input style={inp} value={cur.label} placeholder="AutoConf" onChange={(e) => upd({ label: e.target.value })} /></div>
            <div>
              <label style={lbl}>Tutorial "Encontre o link" (texto, emoji, imagem — como na wiki)</label>
              <RichEditor key={cur.key ?? `new-${sel}`} html={cur.tutorial_html} onChange={(html) => upd({ tutorial_html: html })} />
            </div>
            <button onClick={() => { setFontes((p) => p.filter((_, i) => i !== sel)); setSel(null); }} style={{ ...ghost, color: "var(--danger)", alignSelf: "flex-start" }}>Excluir fonte</button>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: 24, textAlign: "center", border: "1px dashed var(--line)", borderRadius: 10 }}>Selecione ou crie uma fonte.</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => save.mutate()} disabled={!dirty || save.isPending || fontes.some((f) => !f.label.trim())} style={{ ...red, opacity: dirty ? 1 : 0.5 }}>{save.isPending ? "Salvando..." : "Salvar"}</button>
        <SavedNote show={save.isSuccess && !dirty} />
        {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
      </div>
    </div>
  );
}
